import React from 'react';
import {
  BookOpen,
  FileText,
  Folder,
  Home,
  LogOut,
  Settings,
  type LucideIcon,
  Users,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import '../../assets/fonts/typography.css';
import '../../assets/fonts/fonts.css';
import '../../assets/colors/colors.css';
import '../../assets/colors/gradients.css';

import OwlIcon from '../../assets/icons/owl_filled.svg';

interface NavigationItem {
  icon: LucideIcon;
  name: string;
  path: string;
}

const sidebarBrandTextStyle = {
  fontFamily: "'DM Sans', sans-serif",
};

const sidebarBrandTitleStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: '1.2',
};

const sidebarBrandSubtitleStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '10px',
  fontWeight: 500,
  lineHeight: '1.2',
};

const sidebarNavTextStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '1.2',
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  // Student navigation items
  const studentNavItems: NavigationItem[] = [
    { name: 'Home', icon: Home, path: '/' },
    {
      name: 'Project',
      icon: Folder,
      path: '/project',
    },
    {
      name: 'Courses',
      icon: BookOpen,
      path: '/courses',
    },
    { name: 'Team', icon: Users, path: '/team' },
  ];

  // Instructor navigation items
  const instructorNavItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      icon: Home,
      path: '/instructor',
    },
    {
      name: 'Courses',
      icon: BookOpen,
      path: '/instructor/courses',
    },
    {
      name: 'Students',
      icon: Users,
      path: '/instructor/students',
    },
    {
      name: 'Submissions',
      icon: FileText,
      path: '/instructor/submissions',
    },
  ];

  // Admin navigation items
  const adminNavItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      icon: Home,
      path: '/admin',
    },
    {
      name: 'Users',
      icon: Users,
      path: '/admin/users',
    },
    {
      name: 'Courses',
      icon: BookOpen,
      path: '/admin/courses',
    },
    {
      name: 'Submissions',
      icon: FileText,
      path: '/admin/submissions',
    },
    {
      name: 'Projects',
      icon: Folder,
      path: '/admin/projects',
    },
  ];

  // Determine which navigation items to show based on user role
  const navigationItems =
    user?.role === 'admin'
      ? adminNavItems
      : user?.role === 'instructor'
        ? instructorNavItems
        : studentNavItems;

  const isActive = (path: string) => {
    // exact match for home so it doesn't light up everywhere
    if (path === '/') return location.pathname === '/';

    // For instructor dashboard, match exact path only (not child routes)
    if (path === '/instructor') {
      return location.pathname === '/instructor';
    }

    // For instructor students, match exact or child routes
    if (path === '/instructor/students') {
      return (
        location.pathname === '/instructor/students' ||
        location.pathname.startsWith('/instructor/students/')
      );
    }
    // For instructor courses, match exact or child routes
    if (path === '/instructor/courses') {
      return (
        location.pathname === '/instructor/courses' ||
        location.pathname.startsWith('/instructor/courses/')
      );
    }

    // For instructor submissions, match exact or child routes
    if (path === '/instructor/submissions') {
      return (
        location.pathname === '/instructor/submissions' ||
        location.pathname.startsWith('/instructor/submissions/')
      );
    }

    // For admin routes, match exact or child routes
    if (path === '/admin') {
      return (
        location.pathname === '/admin' &&
        !location.pathname.startsWith('/admin/')
      );
    }

    // active if exact match OR any child route (e.g. /project/new)
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  return (
    <aside
      className="card-drop-shadow 
            w-14 sm:w-52 md:w-56 lg:w-56 xl:w-56
            px-1 sm:px-2 
            py-6 sm:py-8 
            flex flex-col justify-between bg-white min-h-screen
            transition-all duration-300 ease-in-out"
    >
      <div
        className="flex flex-col"
        style={{ gap: 'clamp(2rem, 4vh, 2.5rem)' }}
      >
        {/* Logo Section */}
        <div>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:items-start">
            <div className="p-1 sm:p-2 w-12 sm:w-16 h-12 sm:h-16 flex flex-col items-center justify-center">
              <img
                src={OwlIcon}
                alt="ILA"
                className="w-8 sm:w-10 h-8 sm:h-10"
              />
            </div>
            <div className="hidden min-w-0 pt-2 sm:flex sm:flex-col" style={sidebarBrandTextStyle}>
              <span className="truncate text-[#1E2A3A]" style={sidebarBrandTitleStyle}>
                NTU DSAIR
              </span>
              <span className="truncate text-[#94A3B8]" style={sidebarBrandSubtitleStyle}>
                Learning Analytics
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex w-full flex-col h-fit">
          <nav>
            <ul>
              {navigationItems.map((item) => {
                const Icon = item.icon;

                return (
                  <li key={item.name}>
                  <Link
                    to={item.path}
                    className={`flex items-center justify-center sm:justify-start 
                                    px-2 sm:px-4 
                                    py-3 sm:py-3 
                                    rounded-lg 
                                    ${
                                      isActive(item.path)
                                        ? 'subtitle-2 text-highlight-blue bg-tab-selected'
                                        : 'subtitle-2 hover:bg-gray-100'
                                    }`}
                  >
                    <Icon
                      aria-hidden="true"
                      size={22}
                      strokeWidth={2}
                      className={isActive(item.path) ? 'text-highlight-blue' : 'text-grey-80'}
                    />
                    <span className="hidden sm:inline sm:ml-3">
                      <span style={sidebarNavTextStyle}>{item.name}</span>
                    </span>
                  </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>

      <div className="flex-grow min-h-[clamp(1rem,10vh,2rem)]" />

      {/* Bottom Section */}
      <div className="flex w-full flex-col h-fit">
        <ul>
          <li>
            <Link
              to="/settings"
              className={`flex items-center justify-center sm:justify-start 
                        px-2 sm:px-4 
                        py-3 sm:py-3 
                        rounded-lg subtitle-2 
                        ${
                          isActive('/settings')
                            ? 'text-highlight-blue bg-tab-selected'
                            : 'hover:bg-gray-100'
                        }`}
            >
              <Settings
                aria-hidden="true"
                size={22}
                strokeWidth={2}
                className={isActive('/settings') ? 'text-highlight-blue' : 'text-grey-80'}
              />
              <span className="hidden sm:inline sm:ml-3" style={sidebarNavTextStyle}>
                Settings
              </span>
            </Link>
          </li>
          <li>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center sm:justify-start 
                        px-2 sm:px-4 
                        py-3 sm:py-3 
                        rounded-lg subtitle-2 text-highlight-red hover:bg-red-50"
            >
              <LogOut aria-hidden="true" size={22} strokeWidth={2} />
              <span className="hidden sm:inline sm:ml-3" style={sidebarNavTextStyle}>
                Logout
              </span>
            </button>
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
